import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import MainLayout from "../../layouts/MainLayout"
import {
  createCustomer,
  deleteCustomer,
  getCustomerById,
  getCustomers,
  getNextCustomerCode,
  updateCustomer,
} from "../../services/customerService"

const defaultFormData = {
  company_id: 1,
  code: "",
  name: "",
  customer_type: "retail",
  phone: "",
  email: "",
  address: "",
  city: "",
  province: "",
  country: "DRC",
  contact_person: "",
  tax_number: "",
  credit_limit: 0,
  current_debt: 0,
  source_system: "LOCAL",
  external_code: "",
  is_active: true,
}

const customerTypes = [
  { value: "retail", label: "Retail" },
  { value: "distributor", label: "Distributor" },
  { value: "corporate", label: "Corporate" },
  { value: "wholesale", label: "Wholesale" },
]

function CustomersPage() {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [open, setOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [formData, setFormData] = useState(defaultFormData)

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedType, setSelectedType] = useState("")
  const [selectedCity, setSelectedCity] = useState("")

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const loadCustomers = async () => {
    try {
      setLoading(true)
      const data = await getCustomers()
      setCustomers(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Error loading customers:", error)
      showSnackbar("Failed to load customers", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCustomers()
  }, [])

  const resetForm = () => {
    setSelectedCustomer(null)
    setFormData(defaultFormData)
  }

  const openAddDialog = async () => {
    try {
      resetForm()
      const response = await getNextCustomerCode()
      setFormData({
        ...defaultFormData,
        code: response?.code || "",
      })
      setOpen(true)
    } catch (error) {
      console.error(error)
      showSnackbar("Failed to generate customer code", "error")
    }
  }

  const openEditDialog = async (customerId) => {
    try {
      const customer = await getCustomerById(customerId)
      setSelectedCustomer(customer)
      setFormData({
        company_id: customer.company_id ?? 1,
        code: customer.code || "",
        name: customer.name || "",
        customer_type: customer.customer_type || "retail",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        city: customer.city || "",
        province: customer.province || "",
        country: customer.country || "DRC",
        contact_person: customer.contact_person || "",
        tax_number: customer.tax_number || "",
        credit_limit: Number(customer.credit_limit || 0),
        current_debt: Number(customer.current_debt || 0),
        source_system: customer.source_system || "LOCAL",
        external_code: customer.external_code || "",
        is_active:
          typeof customer.is_active === "boolean" ? customer.is_active : true,
      })
      setOpen(true)
    } catch (error) {
      console.error(error)
      showSnackbar("Failed to load customer details", "error")
    }
  }

  const handleClose = () => {
    if (saving) return
    setOpen(false)
    resetForm()
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "is_active"
          ? value === "true" || value === true
          : ["company_id", "credit_limit", "current_debt"].includes(name)
          ? Number(value)
          : value,
    }))
  }

  const validateForm = () => {
    if (!formData.code?.trim()) {
      showSnackbar("Code is required", "warning")
      return false
    }

    if (!formData.name?.trim()) {
      showSnackbar("Customer name is required", "warning")
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    const payload = {
      company_id: Number(formData.company_id || 1),
      code: formData.code.trim(),
      name: formData.name.trim(),
      customer_type: formData.customer_type,
      phone: formData.phone?.trim() || null,
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      city: formData.city?.trim() || null,
      province: formData.province?.trim() || null,
      country: formData.country?.trim() || "DRC",
      contact_person: formData.contact_person?.trim() || null,
      tax_number: formData.tax_number?.trim() || null,
      credit_limit: Number(formData.credit_limit || 0),
      current_debt: Number(formData.current_debt || 0),
      source_system: formData.source_system?.trim() || "LOCAL",
      external_code: formData.external_code?.trim() || null,
      is_active: Boolean(formData.is_active),
    }

    try {
      setSaving(true)

      if (selectedCustomer?.id) {
        await updateCustomer(selectedCustomer.id, payload)
        showSnackbar("Customer updated successfully")
      } else {
        await createCustomer(payload)
        showSnackbar("Customer created successfully")
      }

      await loadCustomers()
      handleClose()
    } catch (error) {
      console.error(error)
      const message =
        error?.response?.data?.detail || "Failed to save customer"
      showSnackbar(message, "error")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (customerId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this customer?"
    )
    if (!confirmed) return

    try {
      await deleteCustomer(customerId)
      showSnackbar("Customer deleted successfully")
      await loadCustomers()
    } catch (error) {
      console.error(error)
      const message =
        error?.response?.data?.detail || "Failed to delete customer"
      showSnackbar(message, "error")
    }
  }

  const handleView = async (customerId) => {
    await openEditDialog(customerId)
  }

  const uniqueCities = useMemo(() => {
    return [...new Set(customers.map((c) => c.city).filter(Boolean))].sort()
  }, [customers])

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const q = searchTerm.trim().toLowerCase()

      const matchesSearch =
        !q ||
        customer.code?.toLowerCase().includes(q) ||
        customer.name?.toLowerCase().includes(q) ||
        customer.phone?.toLowerCase().includes(q) ||
        customer.email?.toLowerCase().includes(q)

      const matchesType =
        !selectedType || customer.customer_type === selectedType

      const matchesCity = !selectedCity || customer.city === selectedCity

      return matchesSearch && matchesType && matchesCity
    })
  }, [customers, searchTerm, selectedType, selectedCity])

  const resetFilters = () => {
    setSearchTerm("")
    setSelectedType("")
    setSelectedCity("")
  }

  return (
    <MainLayout>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" mb={0.5}>
              Customers List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View all customers, search quickly, and filter by type or city.
            </Typography>
          </Box>

          <Button variant="contained" onClick={openAddDialog}>
            + New Customer
          </Button>
        </Box>

        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              fullWidth
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <TextField
              select
              sx={{ minWidth: { xs: "100%", md: 180 } }}
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <MenuItem value="">Type</MenuItem>
              {customerTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              sx={{ minWidth: { xs: "100%", md: 180 } }}
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <MenuItem value="">City</MenuItem>
              {uniqueCities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </TextField>

            <Button variant="outlined" onClick={resetFilters}>
              Reset
            </Button>
          </Stack>
        </Paper>

        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Province</TableCell>
                  <TableCell>Current Debt</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary">
                        Loading customers...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary">
                        No customers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.code}</TableCell>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={customer.customer_type || "-"}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{customer.phone || "-"}</TableCell>
                      <TableCell>{customer.city || "-"}</TableCell>
                      <TableCell>{customer.province || "-"}</TableCell>
                      <TableCell>
                        {Number(customer.current_debt || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {customer.is_active ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Inactive" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleView(customer.id)}
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openEditDialog(customer.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(customer.id)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {selectedCustomer ? "Edit Customer" : "Add New Customer"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Customer Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                select
                label="Customer Type"
                name="customer_type"
                value={formData.customer_type}
                onChange={handleChange}
                fullWidth
              >
                {customerTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
            />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Province"
                name="province"
                value={formData.province}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Contact Person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="Tax Number"
                name="tax_number"
                value={formData.tax_number}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Credit Limit"
                name="credit_limit"
                type="number"
                value={formData.credit_limit}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: "0.01" }}
              />

              <TextField
                label="Current Debt"
                name="current_debt"
                type="number"
                value={formData.current_debt}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Stack>

            <Divider />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Source System"
                name="source_system"
                value={formData.source_system}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                label="External Code"
                name="external_code"
                value={formData.external_code}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                select
                label="Status"
                name="is_active"
                value={String(formData.is_active)}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : selectedCustomer ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default CustomersPage